import { AxiosError } from 'axios';
import Grid from '@mui/material/Grid';
import { useQuery } from '@tanstack/react-query';
import { DataDisplay } from '@components/DataDisplay';
import { QueryStateRouter } from '@components/QueryStateRouter';
import { ScheduledPayments } from '@components/ScheduledPayments';
import { useI18n } from '@hooks/useI18n/useI18n';
import { useModal } from '@hooks/useModal';
import { ModalStatusRouter } from '@hooks/useModal/components';
import { ModalSize } from '@hooks/useModal/components/enums';
import { getModalDetails } from '@hooks/useModal/getModalDetails';
import { Modal } from '@hooks/useModal/types';
import { IOpsPortalEarlySettlement } from '@schemas/opsPortal/types/earlySettlement';
import { QueryKeys } from '@utils/constants';
import { Endpoints } from '@utils/enums';
import { fetchDataFromEndpoint } from '@utils/fetchDataFromEndpoint';
import { getEarlySettlementDisplayDataList } from './getEarlySettlementDisplayDataList';
import { EarlySettlementDataGrid, NewSettlementScheduleGrid } from './styles';
import { IEarlySettlementDisplayModal } from './types';
import { useGetEarlySettlementButtons } from './useGetEarlySettlementButtons';

export const useEarlySettlementQuoteDisplayModal = (
  props: IEarlySettlementDisplayModal,
): Modal => {
  const { earlySettlementQuoteUuid, externalContractUUID } = props;

  const { getLocaleCurrencyValue, getLocaleDate, translate } = useI18n();

  const modal = useModal({
    sharedProps: props,
    modalName: 'earlySettlementsDisplayModal',
    permissions: ['ops_portal.fetch_early_settlement'],
    buttonTranslationKey: 'OPS_PORTAL.BUTTON.VIEW',
  });

  const {
    data: earlySettlement,
    error: earlySettlementsDisplayError,
    isLoading: isEarlySettlementsDisplayLoading,
  } = useQuery<IOpsPortalEarlySettlement, AxiosError>(
    [QueryKeys.EARLY_SETTLEMENT_DISPLAY, earlySettlementQuoteUuid],
    () =>
      fetchDataFromEndpoint(
        `${Endpoints.FetchEarlySettlement}/${earlySettlementQuoteUuid}`,
      ),
    {
      enabled: Boolean(earlySettlementQuoteUuid),
    },
  );

  const { isSubmitting, modalButtons } = useGetEarlySettlementButtons({
    closeModal: modal?.closeModal,
    earlySettlement,
    earlySettlementQuoteUuid,
    externalContractUUID,
  });

  const formID = 'earlySettlementForm';

  const dataList = earlySettlement
    ? getEarlySettlementDisplayDataList({
        earlySettlement,
        getLocaleCurrencyValue,
        getLocaleDate,
        translate,
      })
    : [];

  const modalContent = (
    <ModalStatusRouter isLoading={isSubmitting}>
      <QueryStateRouter
        error={earlySettlementsDisplayError}
        isLoading={isEarlySettlementsDisplayLoading}
        loadingMessage="STATUS.LOADING"
      >
        <Grid container={true}>
          <EarlySettlementDataGrid
            item={true}
            lg={earlySettlement?.new_schedule ? 8 : 12}
            xs={12}
          >
            <h3>{translate('OPS_PORTAL.TITLE.QUOTE_DETAILS')}</h3>
            <DataDisplay dataList={dataList} />
          </EarlySettlementDataGrid>
          {earlySettlement?.new_schedule ? (
            <NewSettlementScheduleGrid item={true} lg={4} xs={12}>
              <h3>{translate('OPS_PORTAL.LABEL.NEW_SCHEDULE')}</h3>
              <ScheduledPayments
                payments={
                  earlySettlement?.new_schedule.map(
                    ({ amount: { amount, currency }, due_date: dueDate }) => ({
                      dueDate: getLocaleDate({
                        date: dueDate,
                      }),
                      paymentAmount:
                        getLocaleCurrencyValue({
                          value: amount,
                          currency,
                        }) ?? '',
                    }),
                  ) ?? []
                }
              />
            </NewSettlementScheduleGrid>
          ) : null}
        </Grid>
      </QueryStateRouter>
    </ModalStatusRouter>
  );

  const modalProps = {
    customButtons: modalButtons,
    formID,
    isDangerousSubmit: true,
    isLoading: false,
    modalContent,
    title: translate('OPS_PORTAL.TITLE.EARLY_SETTLEMENT_QUOTE'),
    width: earlySettlement?.new_schedule ? ModalSize.Md : ModalSize.Sm,
  };

  return getModalDetails({ modal, modalProps });
};
